import React, { useEffect, useRef, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from "recharts";
import {
  getAssetInventoryCharts,
  getAssetInventoryMetrics,
  getAssetTrackerData,
  getRetestDashboard,
  clearDashboardApiCache
} from "../../utils/api/endpoints/dashboard/dashboardApi";
import usePageTitle from "../../utils/hooks/usePageTitle";
import useUnifiedBusinessDashboard from "../../utils/hooks/dashboard/useUnifiedBusinessDashboard";
import { FiRefreshCw, FiCalendar, FiChevronDown, FiLoader } from "react-icons/fi";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getVantaStatus, triggerVantaSync } from "../../utils/api/endpoints/vanta";
import { useExecutiveSummaryData } from "../../utils/hooks/dashboard/useExecutiveSummaryData";
import SeverityDistributionChart from "../../components/dashboard/business/SeverityDistributionChart";
import RiskHeatmap from "../../components/dashboard/business/RiskHeatmap";
import ActiveProgramsTable from "../../components/dashboard/business/ActiveProgramsTable";
import VulnerabilityTrendChart from "../../components/dashboard/business/VulnerabilityTrendChart";
import FindingsBySeverityChart from "../../components/dashboard/business/FindingsBySeverityChart";
import ModernStatSection from "../../components/dashboard/business/ModernStatSection";
import VantaIntegrationBanner from "../../components/dashboard/business/VantaIntegrationBanner";
import ChartFilter, { TimeRange, getPeriodLabel } from "../../components/dashboard/business/ChartFilter";
import { MdInfo } from "react-icons/md";
import moment from "moment";

const RetestDashboardSection = React.lazy(() => import("../../components/dashboard/business/RetestDashboardSection"));
const AssetInventoryStats = React.lazy(() => import("../../components/dashboard/business/AssetInventoryStats"));
const AssetInventoryCharts = React.lazy(() => import("../../components/dashboard/business/AssetInventoryCharts"));
const AssetTrackerTable = React.lazy(() => import("../../components/dashboard/business/AssetTrackerTable"));

// Animation variants for page sections
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 }
  }
};

// Loading Skeleton components
const StatsSkeleton = () => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
    {[...Array(4)].map((_, index) => (
      <div
        key={index}
        className="h-[110px] animate-pulse rounded-xl bg-gray-200 shadow-sm"
      ></div>
    ))}
  </div>
);

const ChartSkeleton = () => (
  <div className="h-[380px] animate-pulse rounded-xl bg-gray-200 shadow-sm"></div>
);

// --- Inline: Data Classification Threat Map ---
const THREAT_SEVERITY_COLORS = { critical: "#ef4444", high: "#f97316", medium: "#eab308", low: "#22c55e", info: "#64748b" };

const ThreatMapTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border border-slate-100 shadow-lg rounded-lg text-xs font-bold text-slate-700">
        <p className="mb-2 text-sm text-slate-900">{label}</p>
        {payload.map((entry: any, i: number) => (
          <div key={i} className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="capitalize">{entry.name}:</span>
            <span>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

interface DataClassificationThreatMapProps {
  isSubscriptionOnly?: boolean;
  subscriptionDates?: { startDate: string; endDate: string } | null;
  selectedYear?: number | null;
}

const DataClassificationThreatMap: React.FC<DataClassificationThreatMapProps> = React.memo(({
  isSubscriptionOnly = false,
  subscriptionDates,
  selectedYear = null
}) => {
  const [tmData, setTmData] = useState<any[]>([]);
  const [tmLoading, setTmLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>(
    (selectedYear !== null || isSubscriptionOnly) ? 'yearly' : 'all'
  );
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    if (selectedYear !== null || isSubscriptionOnly) {
      setTimeRange('yearly');
    } else {
      setTimeRange('all');
    }
  }, [selectedYear, isSubscriptionOnly]);

  const currentYear = new Date().getFullYear();
  const effectiveTimeRange = (selectedYear && selectedYear !== currentYear)
    ? 'yearly'
    : timeRange;

  useEffect(() => {
    let isMounted = true;
    if (tmData.length === 0) setTmLoading(true);
    getAssetInventoryCharts(effectiveTimeRange, isSubscriptionOnly, selectedYear)
      .then((res: any) => {
        if (isMounted && res?.success && res.data?.classificationThreatMap) {
          setTmData(res.data.classificationThreatMap);
        }
      })
      .catch(() => { })
      .finally(() => {
        if (isMounted) setTmLoading(false);
      });
    return () => { isMounted = false; };
  }, [effectiveTimeRange, isSubscriptionOnly, selectedYear]);

  if (tmLoading && tmData.length === 0) {
    return <div className="w-full h-[350px] bg-white rounded-xl border border-slate-100 shadow-sm animate-pulse flex items-center justify-center"><span className="text-slate-400 text-sm">Loading...</span></div>;
  }
  if (!tmData || tmData.length === 0) return null;

  return (
    <motion.div
      className="w-full bg-white rounded-xl shadow-sm border border-slate-100 flex flex-col relative"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <AnimatePresence>
        {tmLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px] rounded-xl"
          >
            <FiLoader className="w-8 h-8 text-blue-500 animate-spin" />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="px-6 py-4 border-b border-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex flex-col min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-800 break-words min-w-0">
            Data Classification Threat Map
          </h3>
          <span className="text-[11px] text-slate-400 font-semibold mt-0.5">
            {(selectedYear && selectedYear !== currentYear) ? `Year ${selectedYear}` : getPeriodLabel(timeRange, isSubscriptionOnly, subscriptionDates?.startDate)}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative flex-shrink-0">
            <MdInfo
              className="h-4 w-4 text-slate-300 hover:text-blue-500 cursor-help transition-colors"
              onMouseEnter={() => setShowInfo(true)}
              onMouseLeave={() => setShowInfo(false)}
            />
            <AnimatePresence>
              {showInfo && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute right-0 top-full mt-2 z-50 w-64 bg-[#010D2C] text-white p-3 rounded-xl text-[11px] font-medium shadow-xl pointer-events-none leading-relaxed"
                >
                  Displays a breakdown of vulnerabilities by severity (Critical, High, etc.) categorized across your asset data classifications (e.g., Confidential, Public).
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {(!selectedYear || selectedYear === currentYear) && (
            <ChartFilter
              active={timeRange}
              onChange={setTimeRange}
              isSubscriptionOnly={isSubscriptionOnly}
              subscriptionStartDate={subscriptionDates?.startDate}
              supportsAllTime={!selectedYear}
              selectedYear={selectedYear}
            />
          )}
        </div>
      </div>

      <div className="pt-6 px-6 pb-3 flex-1 flex flex-col relative min-h-[380px]">
        <ResponsiveContainer width="100%" height={380} debounce={50}>
          <BarChart layout="vertical" data={tmData} margin={{ top: 5, right: 30, left: 120, bottom: 50 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
            <XAxis
              type="number"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#64748b', fontWeight: 600 }}
              dy={10}
              label={{
                value: 'Vulnerability Count',
                position: 'insideBottom',
                offset: -10,
                style: { fill: '#6B7280', fontSize: 10, fontWeight: 700 }
              }}
            />
            <YAxis
              type="category"
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#64748b', fontWeight: 600 }}
              dx={-10}
              width={110}
              label={{
                value: 'Data Classification',
                angle: -90,
                position: 'insideLeft',
                offset: -105,
                style: { fill: '#6B7280', fontSize: 10, fontWeight: 700, textAnchor: 'middle' }
              }}
            />
            <RechartsTooltip content={<ThreatMapTooltip />} cursor={{ fill: 'transparent' }} />
            <Legend
              verticalAlign="bottom"
              align="center"
              iconType="square"
              wrapperStyle={{ fontSize: '12px', fontWeight: 600, bottom: '5px' }}
            />
            <Bar dataKey="critical" name="Critical" stackId="a" fill={THREAT_SEVERITY_COLORS.critical} barSize={32} animationDuration={400} />
            <Bar dataKey="high" name="High" stackId="a" fill={THREAT_SEVERITY_COLORS.high} barSize={32} animationDuration={400} />
            <Bar dataKey="medium" name="Medium" stackId="a" fill={THREAT_SEVERITY_COLORS.medium} barSize={32} animationDuration={400} />
            <Bar dataKey="low" name="Low" stackId="a" fill={THREAT_SEVERITY_COLORS.low} barSize={32} animationDuration={400} />
            <Bar dataKey="info" name="Info" stackId="a" fill={THREAT_SEVERITY_COLORS.info} barSize={32} radius={[0, 4, 4, 0]} animationDuration={400} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
});
// --- End Inline Component ---

const BUSINESS_ROLES = [
  Number(UserRole.BUSINESS),
  Number(UserRole.DEVELOPER),
  Number(UserRole.BUSINESS_MANAGER),
  Number(UserRole.BUSINESS_ADMINISTRATOR)
];

const ModernBusinessDashboard: React.FC = () => {
  usePageTitle("Business Dashboard");
  const isLoadingRef = useRef(false);
  const [isSubscriptionOnly, setIsSubscriptionOnly] = useState(true);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<"summary" | "assets" | "retests">("summary");

  const {
    metrics,
    retestActions,
    hasActiveSubscription,
    subscriptionDates,
    firstProgramYear,
    loading: unifiedLoading,
    error: unifiedError,
    refetch: refetchUnified
  } = useUnifiedBusinessDashboard(undefined, isSubscriptionOnly, selectedYear);

  const {
    data: executiveData,
    refetch: refetchExecutive
  } = useExecutiveSummaryData(isSubscriptionOnly, selectedYear, activeTab === "summary");

  const topActivePrograms = useMemo(() => {
    if (!executiveData?.activePrograms) return [];
    return [...executiveData.activePrograms].sort((a, b) => b.program_id - a.program_id).slice(0, 5);
  }, [executiveData?.activePrograms]);

  // Only for debugging - single logger useEffect
  useEffect(() => {
    if (!isLoadingRef.current) {
      console.log("Dashboard data loaded:", {
        metrics
      });
    }
  }, [metrics]);

  const { id: userId, role } = useUserCredentials();
  const [vantaConnected, setVantaConnected] = useState<boolean>(false);
  const [pushLoading, setPushLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStatus = async () => {
      if (userId && BUSINESS_ROLES.includes(Number(role))) {
        try {
          const status = await getVantaStatus(userId);
          // Prefer explicit vanta_connected; fall back to connected if present
          const connected = (status?.vanta_connected === true) || (status?.connected === true);
          setVantaConnected(connected);
        } catch (e) {
          // Default to not connected if status fails
          setVantaConnected(false);
        }
      }
    };
    fetchStatus();
  }, [role, userId]);

  const handleConnectClick = () => {
    navigate("/dashboard/settings/integrations");
  };

  const handlePushToVanta = async () => {
    if (!userId) return;
    setPushLoading(true);
    try {
      await triggerVantaSync(userId); // pass explicit user id to avoid any ambiguity
      toast.success("Sync to Vanta triggered");
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to trigger sync");
    } finally {
      setPushLoading(false);
    }
  };

  useEffect(() => {
    if (!unifiedLoading && hasActiveSubscription === false && isSubscriptionOnly) {
      setIsSubscriptionOnly(false);
    }
  }, [unifiedLoading, hasActiveSubscription, isSubscriptionOnly]);

  // Background Idle Prefetch: Once the executive summary is loaded and the user
  // is browsing, prefetch the other tabs into the memory cache during idle time.
  // Tab switches to "Asset Inventory" or "Retests" then render in 0ms with zero loading wait.
  useEffect(() => {
    if (!executiveData?.severityDistribution && !metrics) return;

    const prefetchTimer = setTimeout(() => {
      // Preload lazy tab code bundles in background so tab click has 0ms wait
      import("../../components/dashboard/business/AssetInventoryStats");
      import("../../components/dashboard/business/AssetInventoryCharts");
      import("../../components/dashboard/business/AssetTrackerTable");
      import("../../components/dashboard/business/RetestDashboardSection");

      // Preload data into in-memory cache
      getAssetInventoryMetrics(isSubscriptionOnly, selectedYear).catch(() => {});
      getAssetInventoryCharts('yearly', isSubscriptionOnly, selectedYear).catch(() => {});
      getAssetTrackerData(isSubscriptionOnly, selectedYear).catch(() => {});
      getRetestDashboard('all', isSubscriptionOnly, selectedYear).catch(() => {});
      getRetestDashboard('yearly', isSubscriptionOnly, selectedYear).catch(() => {});
    }, 1500);

    return () => clearTimeout(prefetchTimer);
  }, [executiveData?.severityDistribution, metrics, isSubscriptionOnly, selectedYear]);

  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const yearDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(event.target as Node)) {
        setIsYearDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleRetryAll = () => {
    if (isLoadingRef.current) return;

    isLoadingRef.current = true;
    clearDashboardApiCache();
    Promise.all([
      refetchExecutive(),
      refetchUnified()
    ]).finally(() => {
      isLoadingRef.current = false;
    });
  };

  // Generate dynamically based on firstProgramYear to currentYear (Hook called unconditionally at top of component)
  const currentYear = new Date().getFullYear();
  const availableYears = useMemo(() => {
    const years: number[] = [];
    if (firstProgramYear) {
      for (let yr = currentYear; yr >= firstProgramYear; yr--) {
        years.push(yr);
      }
    } else {
      years.push(currentYear);
    }
    return years;
  }, [firstProgramYear, currentYear]);

  // Handle critical errors when both data sources fail
  if (unifiedError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
        <div className="text-center">
          <h2 className="mb-2 text-xl font-bold text-gray-800">Oops! Something went wrong</h2>
          <p className="text-gray-600">
            Failed to load critical dashboard data. Please try again.
          </p>
          <button
            onClick={handleRetryAll}
            disabled={isLoadingRef.current}
            className="mt-4 rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:bg-blue-300"
          >
            {isLoadingRef.current ? "Retrying..." : "Retry All"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="min-h-screen bg-slate-50 p-4 md:p-6 max-w-full mx-auto"
      style={{ fontFamily: "'Montserrat', sans-serif" }}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="mb-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">Security Dashboard</h1>
        </div>

        {/* Header Actions */}
        <div className="flex items-center gap-3 flex-wrap">


          <button
            onClick={handleRetryAll}
            disabled={isLoadingRef.current}
            className="p-2.5 rounded-xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:bg-slate-50 transition-all"
            title="Refresh All"
          >
            <FiRefreshCw className={`h-5 w-5 ${isLoadingRef.current ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Navy Blue Tab Navigation and Toggle */}
      <div className="mb-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="bg-[#010D2C] py-2.5 px-2 rounded-xl flex w-full max-w-2xl items-center gap-1 shadow-2xl shadow-slate-200">
          <button
            onClick={() => setActiveTab("summary")}
            className={`flex-1 py-3 rounded-lg text-xs font-normal transition-all ${activeTab === "summary"
              ? "text-white"
              : "text-slate-400 hover:text-white hover:bg-white/5"
              } relative`}
          >
            Executive Summary
            {activeTab === "summary" && (
              <motion.div
                layoutId="activeTab"
                className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#58CC02] mx-10 rounded-t-sm"
              />
            )}
          </button>
          <button
            onClick={() => setActiveTab("assets")}
            className={`flex-1 py-3 rounded-lg text-xs font-normal transition-all ${activeTab === "assets"
              ? "text-white"
              : "text-slate-400 hover:text-white hover:bg-white/5"
              } relative`}
          >
            Asset Inventory
            {activeTab === "assets" && (
              <motion.div
                layoutId="activeTab"
                className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#58CC02] mx-10 rounded-t-sm"
              />
            )}
          </button>
          <button
            onClick={() => setActiveTab("retests")}
            className={`flex-1 py-3 rounded-lg text-xs font-normal transition-all ${activeTab === "retests"
              ? "text-white"
              : "text-slate-400 hover:text-white hover:bg-white/5"
              } relative`}
          >
            Retest Dashboard
            {activeTab === "retests" && (
              <motion.div
                layoutId="activeTab"
                className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#58CC02] mx-10 rounded-t-sm"
              />
            )}
          </button>
        </div>

        <div className="flex items-center gap-3">
          {/* Premium Year Select Dropdown (Only visible in ALL TIME mode) */}
          {!isSubscriptionOnly && (
            <div className="relative" ref={yearDropdownRef}>
              <button
                onClick={() => setIsYearDropdownOpen(!isYearDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl shadow-sm hover:border-slate-300 hover:bg-slate-50 transition-all text-xs font-bold text-slate-700 h-[52px]"
              >
                <FiCalendar className="h-4 w-4 text-slate-400" />
                <span>
                  {selectedYear ? `Year: ${selectedYear}` : "All Time"}
                </span>
                <FiChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isYearDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {isYearDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-2 z-50 w-44 bg-[#010D2C] border border-slate-800 rounded-xl shadow-xl p-1.5 space-y-1"
                  >
                    <button
                      onClick={() => {
                        setSelectedYear(null);
                        setIsYearDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-xs font-bold rounded-lg transition-all ${selectedYear === null
                        ? "bg-[#58CC02] text-white shadow-sm"
                        : "text-slate-300 hover:bg-white/10 hover:text-white"
                        }`}
                    >
                      All Time
                    </button>
                    {availableYears.map((year) => (
                      <button
                        key={year}
                        onClick={() => {
                          setSelectedYear(Number(year));
                          setIsYearDropdownOpen(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-xs font-bold rounded-lg transition-all ${selectedYear === Number(year)
                          ? "bg-[#58CC02] text-white shadow-sm"
                          : "text-slate-300 hover:bg-white/10 hover:text-white"
                          }`}
                      >
                        {year}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Subscription Toggle */}
          {hasActiveSubscription && (
            <div className="flex items-center gap-4 px-4 py-2 bg-white border border-slate-200 rounded-xl shadow-sm shrink-0 h-[52px]">
              <button
                onClick={() => {
                  setIsSubscriptionOnly(false);
                  setSelectedYear(null);
                }}
                className={`text-[11px] font-black tracking-tight transition-all ${!isSubscriptionOnly ? 'text-[#58CC02]' : 'text-slate-400 hover:text-slate-500'}`}
              >
                ALL TIME
              </button>

              <button
                onClick={() => {
                  setIsSubscriptionOnly(!isSubscriptionOnly);
                  if (!isSubscriptionOnly) {
                    setSelectedYear(null);
                  }
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${isSubscriptionOnly ? 'bg-[#58CC02]' : 'bg-slate-300'
                  }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${isSubscriptionOnly ? 'translate-x-6' : 'translate-x-1'
                    }`}
                />
              </button>

              <button
                onClick={() => {
                  setIsSubscriptionOnly(true);
                  setSelectedYear(null);
                }}
                className="flex flex-col items-start"
              >
                <span className={`text-[11px] font-black tracking-tight transition-all ${isSubscriptionOnly ? 'text-[#58CC02]' : 'text-slate-400 hover:text-slate-500'}`}>
                  SUBSCRIPTION
                </span>
                {subscriptionDates && (
                  <span className={`text-[9px] font-bold transition-opacity duration-200 ${isSubscriptionOnly ? 'text-slate-500' : 'text-slate-400/60'}`}>
                    {moment(subscriptionDates.startDate).format("MMM D, YYYY")} - {moment(subscriptionDates.endDate).format("MMM D, YYYY")}
                  </span>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence initial={false}>
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          {/* Tab 1: Executive Summary */}
          {activeTab === "summary" && (
            <div className="space-y-4">
              {/* Top Stats Row */}
              {unifiedLoading ? (
                <StatsSkeleton />
              ) : (
                metrics && (
                  <ModernStatSection
                    data={metrics}
                    isSubscriptionOnly={isSubscriptionOnly}
                    selectedYear={selectedYear}
                    subscriptionDates={subscriptionDates}
                  />
                )
              )}

              {/* Main Analysis Flex Layout: Findings and Distribution */}
              <div className="flex flex-col xl:flex-row gap-4 items-stretch">
                {/* Left Column: Findings by Severity and Status (Mounted concurrently to eliminate network waterfall) */}
                <div className="flex-1">
                  <FindingsBySeverityChart
                    isSubscriptionOnly={isSubscriptionOnly}
                    selectedYear={selectedYear}
                    subscriptionStartDate={subscriptionDates?.startDate}
                    enabled={activeTab === "summary"}
                  />
                </div>

                {/* Right Column: Vanta and Severity Distribution */}
                <div className="flex-1 flex flex-col gap-4">
                  {/* Container 1: Vanta Integration */}
                  <div>
                    <VantaIntegrationBanner
                      connected={vantaConnected === true}
                      onPush={handlePushToVanta}
                      onConnect={handleConnectClick}
                      isLoading={pushLoading}
                      disabled={Number(role) !== Number(UserRole.BUSINESS)}
                    />
                  </div>

                  {/* Container 2: Severity Distribution */}
                  {!executiveData?.severityDistribution ? (
                    <ChartSkeleton />
                  ) : (
                    <SeverityDistributionChart
                      data={executiveData.severityDistribution}
                      isSubscriptionOnly={isSubscriptionOnly}
                      selectedYear={selectedYear}
                      subscriptionStartDate={subscriptionDates?.startDate}
                    />
                  )}
                </div>
              </div>

              {/* Tertiary Analysis Row: Vulnerability Trend Line Chart (Full Width) */}
              <div className="w-full">
                {!executiveData?.vulnerabilityTrend ? (
                  <ChartSkeleton />
                ) : (
                  <VulnerabilityTrendChart
                    data={executiveData.vulnerabilityTrend}
                    isSubscriptionOnly={isSubscriptionOnly}
                    selectedYear={selectedYear}
                    subscriptionStartDate={subscriptionDates?.startDate}
                  />
                )}
              </div>

              {/* Bottom Row: Risk Heatmap and Active Programs */}
              <div className="flex flex-col xl:flex-row gap-4 items-stretch h-[550px]">
                {/* Left: Risk Heatmap */}
                <div className="flex-1">
                  {!executiveData?.riskTrend ? (
                    <ChartSkeleton />
                  ) : (
                    <RiskHeatmap
                      data={executiveData.riskTrend}
                      isSubscriptionOnly={isSubscriptionOnly}
                      selectedYear={selectedYear}
                      subscriptionStartDate={subscriptionDates?.startDate}
                    />
                  )}
                </div>

                {/* Right: Active Programs Table */}
                <div className="flex-1">
                  {!executiveData?.activePrograms ? (
                    <ChartSkeleton />
                  ) : (
                    <ActiveProgramsTable programs={topActivePrograms} />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tab 2: Asset Inventory */}
          {activeTab === "assets" && (
            <React.Suspense fallback={<ChartSkeleton />}>
              <div className="space-y-4">
                <AssetInventoryStats
                  isSubscriptionOnly={isSubscriptionOnly}
                  subscriptionDates={subscriptionDates}
                  selectedYear={selectedYear}
                />
                <AssetInventoryCharts isSubscriptionOnly={isSubscriptionOnly} selectedYear={selectedYear} subscriptionStartDate={subscriptionDates?.startDate} />
                <AssetTrackerTable isSubscriptionOnly={isSubscriptionOnly} selectedYear={selectedYear} />
                <DataClassificationThreatMap
                  isSubscriptionOnly={isSubscriptionOnly}
                  subscriptionDates={subscriptionDates}
                  selectedYear={selectedYear}
                />
              </div>
            </React.Suspense>
          )}

          {/* Tab 3: Program Analytics (Retest Dashboard) */}
          {activeTab === "retests" && (
            <React.Suspense fallback={<ChartSkeleton />}>
              <div className="space-y-4">
                <RetestDashboardSection
                  isSubscriptionOnly={isSubscriptionOnly}
                  subscriptionDates={subscriptionDates}
                  selectedYear={selectedYear}
                />
              </div>
            </React.Suspense>
          )}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};

export default ModernBusinessDashboard;